import React from 'react';
import { SystemService } from 'services';
import { Modal, Pagination } from 'components';
import { Globals } from 'utils';
import { CronExpressionParser } from 'cron-parser';
import moment from 'moment';

// ============================================================
// Shapes reales del backend (spec C3)
// ============================================================

interface CronJob {
	job_name?: string;
	enabled?: boolean;
	schedule_description?: string;
	schedule_cron?: string;
	last_run?: string | null;
	last_status?: string | null;
	last_duration_ms?: number | null;
	last_error_message?: string | null;
	is_running_now?: boolean;
	stats_24h?: {
		runs?: number;
		successful?: number;
		failed?: number;
	};
	stats?: {
		runs?: number;
		successful?: number;
		failed?: number;
	};
	last_stats?: {
		processed?: number;
		successful?: number;
		failed?: number;
	};
}

interface CronStatusFull {
	jobs?: CronJob[];
	config?: Record<string, string>;
	global_stats?: {
		total_runs_24h?: number;
		successful_24h?: number;
		failed_24h?: number;
		active_jobs?: number;
		disabled_jobs?: number;
	};
	server_time?: string;
}

interface CronLogRow {
	id?: number;
	job_name?: string;
	cycle_id?: string | null;
	status?: string;
	started_at?: string;
	finished_at?: string | null;
	duration_ms?: number | null;
	total_processed?: number | null;
	successful_updates?: number | null;
	failed_updates?: number | null;
	error_message?: string | null;
	metadata?: any;
	created_at?: string;
	updated_at?: string;
}

interface CronLogsFull {
	logs?: CronLogRow[];
	pagination?: {
		page?: number;
		limit?: number;
		total?: number;
		total_pages?: number;
	};
	filters_applied?: {
		job_name?: string | null;
		status?: string | null;
		from?: string | null;
		to?: string | null;
	};
}

// ============================================================
// Constantes
// ============================================================

// Los 7 jobs conocidos del backend con descripción "humana" para los
// modales y los dropdowns. Si el backend devuelve un job adicional,
// igual se renderiza con su job_name como fallback.
const JOB_CATALOG: Record<string, { label: string; description: string; color: string; configKeys: ConfigKey[] }> = {
	stocks_paid: {
		label: 'Stocks Paid',
		description: 'Actualiza el precio de las acciones para usuarios con plan pagado.',
		color: '#54aa3f',
		configKeys: [
			{ key: 'stocks_paid_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'stocks_paid_interval_minutes', label: 'Intervalo (minutos)', kind: 'number', min: 1 },
			{ key: 'stocks_paid_bulk_chunk_size', label: 'Tamaño de chunk', kind: 'number', min: 1 },
			{ key: 'stocks_paid_delay_between_chunks_ms', label: 'Delay entre chunks (ms)', kind: 'number', min: 0 },
		],
	},
	stocks_free: {
		label: 'Stocks Free',
		description: 'Actualiza el precio de las acciones para usuarios con plan gratuito.',
		color: '#38e788',
		configKeys: [
			{ key: 'stocks_free_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'stocks_free_interval_minutes', label: 'Intervalo (minutos)', kind: 'number', min: 1 },
			{ key: 'stocks_free_bulk_chunk_size', label: 'Tamaño de chunk', kind: 'number', min: 1 },
			{ key: 'stocks_free_delay_between_chunks_ms', label: 'Delay entre chunks (ms)', kind: 'number', min: 0 },
		],
	},
	crypto_paid: {
		label: 'Crypto Paid',
		description: 'Actualiza el precio de las criptomonedas para usuarios pagados.',
		color: '#ef6526',
		configKeys: [
			{ key: 'crypto_paid_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'crypto_paid_interval_hours', label: 'Intervalo (horas)', kind: 'number', min: 1 },
		],
	},
	crypto_free: {
		label: 'Crypto Free',
		description: 'Actualiza el precio de las criptomonedas para usuarios gratuitos.',
		color: '#daa509',
		configKeys: [
			{ key: 'crypto_free_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'crypto_free_interval_hours', label: 'Intervalo (horas)', kind: 'number', min: 1 },
		],
	},
	bonds: {
		label: 'Bonds',
		description: 'Actualiza los precios y rendimientos de bonos.',
		color: '#4d8eff',
		configKeys: [
			{ key: 'bonds_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'bonds_interval_hours', label: 'Intervalo (horas)', kind: 'number', min: 1 },
		],
	},
	interests: {
		label: 'Interests',
		description: 'Procesa los intereses y rendimientos de los activos financieros.',
		color: '#a8870f',
		configKeys: [
			{ key: 'interests_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'interests_interval_hours', label: 'Intervalo (horas)', kind: 'number', min: 1 },
		],
	},
	notifications: {
		label: 'Notifications',
		description: 'Envía las notificaciones programadas (alertas, recordatorios, etc).',
		color: '#9c29ff',
		configKeys: [
			{ key: 'notifications_enabled', label: 'Habilitado', kind: 'bool' },
			{ key: 'notifications_interval_hours', label: 'Intervalo (horas)', kind: 'number', min: 1 },
		],
	},
};

const KNOWN_JOBS = Object.keys(JOB_CATALOG);

interface ConfigKey {
	key: string;
	label: string;
	kind: 'bool' | 'number';
	min?: number;
}

const STATUS_OPTIONS = [
	{ value: '', label: 'Todos' },
	{ value: 'running', label: 'En ejecución' },
	{ value: 'completed', label: 'Completado' },
	{ value: 'failed', label: 'Fallido' },
];

const PAGE_LIMIT = 20;
const REFRESH_DATA_MS = 10 * 1000;
const TICK_MS = 1000;

// ============================================================
// Helpers de formato
// ============================================================

const safeNumber = (v: any): number => {
	const n = Number(v);
	return isNaN(n) ? 0 : n;
}

const statusKind = (status?: string | null, isRunningNow?: boolean): 'ok' | 'warning' | 'critical' | 'running' | 'disabled' | 'unknown' => {
	if (isRunningNow) return 'running';
	if (!status) return 'unknown';
	const s = String(status).toLowerCase();
	if (s === 'ok' || s === 'success' || s === 'completed') return 'ok';
	if (s === 'warning' || s === 'warn' || s === 'partial') return 'warning';
	if (s === 'critical' || s === 'error' || s === 'failed' || s === 'down') return 'critical';
	if (s === 'running' || s === 'in_progress' || s === 'started') return 'running';
	if (s === 'disabled') return 'disabled';
	return 'unknown';
}

const formatDuration = (ms?: number | null): string => {
	if (ms == null || isNaN(Number(ms))) return '—';
	const n = Number(ms);
	if (n < 1000) return `${ n.toFixed(0) } ms`;
	const totalSeconds = n / 1000;
	if (totalSeconds < 60) return `${ totalSeconds.toFixed(1) }s`;
	const mins = Math.floor(totalSeconds / 60);
	const secs = Math.floor(totalSeconds % 60);
	return `${ mins }m ${ secs }s`;
}

const humanizeAgo = (iso?: string | null, now: number = Date.now()): string => {
	if (!iso) return '—';
	const t = moment(iso);
	if (!t.isValid()) return '—';
	const diff = Math.floor((now - t.valueOf()) / 1000);
	if (diff < 0) return 'ahora';
	if (diff < 5) return 'hace instantes';
	if (diff < 60) return `hace ${ diff } seg`;
	const mins = Math.floor(diff / 60);
	if (mins < 60) return `hace ${ mins } min`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `hace ${ hours }h`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `hace ${ days }d`;
	const months = Math.floor(days / 30);
	if (months < 12) return `hace ${ months } mes${ months === 1 ? '' : 'es' }`;
	const years = Math.floor(months / 12);
	return `hace ${ years } año${ years === 1 ? '' : 's' }`;
}

const formatCountdown = (msUntil: number): string => {
	if (msUntil <= 0) return 'Ya';
	const totalSeconds = Math.floor(msUntil / 1000);
	const days = Math.floor(totalSeconds / 86400);
	const hours = Math.floor((totalSeconds % 86400) / 3600);
	const mins = Math.floor((totalSeconds % 3600) / 60);
	const secs = totalSeconds % 60;
	if (days > 0) return `en ${ days }d ${ hours }h`;
	if (hours > 0) return `en ${ hours }h ${ mins }m`;
	if (mins > 0) return `en ${ mins }m ${ secs }s`;
	return `en ${ secs }s`;
}

// Calcula el próximo run a partir de la expresión cron (en UTC) y la hora actual.
// cron-parser v5 expone CronExpressionParser.parse y la opción es `tz`.
const nextRunFromCron = (cronExpr?: string, fromIso?: string): Date | null => {
	if (!cronExpr) return null;
	try {
		const baseDate = fromIso ? new Date(fromIso) : new Date();
		const it = CronExpressionParser.parse(cronExpr, {
			currentDate: baseDate,
			tz: 'UTC',
		} as any);
		return it.next().toDate();
	}
	catch (e) {
		return null;
	}
}

// ============================================================
// Componente
// ============================================================

interface State {
	// data
	status: CronStatusFull | null;
	logs: CronLogsFull | null;
	statusError: boolean;
	logsError: boolean;
	initialLoading: boolean;
	// tick para countdowns (solo re-render, no fetch)
	tick: number;
	lastUpdated: Date | null;
	// filtros tabla logs
	filterJob: string;
	filterStatus: string;
	filterFrom: string;
	filterUntil: string;
	page: number;
	// modal: editar config
	configModalOpen: boolean;
	configModalJob: string | null;
	configForm: Record<string, string>;
	configSaving: boolean;
	// modal: run now
	runModalOpen: boolean;
	runModalJob: string | null;
	runSubmitting: boolean;
	// modal: error detail
	errorModalOpen: boolean;
	errorModalLog: CronLogRow | null;
}

class Cron extends React.Component<{}, State> {

	dataTimer: any = null;
	tickTimer: any = null;
	// Timeouts diferidos disparados tras un "Run Now" exitoso para que
	// el log nuevo aparezca en la tabla sin esperar al tick de 10s.
	runFollowupTimers: any[] = [];
	isMounted_: boolean = false;

	state: State = {
		status: null,
		logs: null,
		statusError: false,
		logsError: false,
		initialLoading: true,
		tick: Date.now(),
		lastUpdated: null,
		filterJob: '',
		filterStatus: '',
		filterFrom: '',
		filterUntil: '',
		page: 1,
		configModalOpen: false,
		configModalJob: null,
		configForm: {},
		configSaving: false,
		runModalOpen: false,
		runModalJob: null,
		runSubmitting: false,
		errorModalOpen: false,
		errorModalLog: null,
	}

	componentDidMount() {
		this.isMounted_ = true;
		this.loadAll(true);

		this.dataTimer = setInterval(() => {
			this.loadAll(false);
		}, REFRESH_DATA_MS);

		this.tickTimer = setInterval(() => {
			if (this.isMounted_) {
				this.setState({ tick: Date.now() });
			}
		}, TICK_MS);
	}

	componentWillUnmount() {
		this.isMounted_ = false;
		if (this.dataTimer) clearInterval(this.dataTimer);
		if (this.tickTimer) clearInterval(this.tickTimer);
		this.dataTimer = null;
		this.tickTimer = null;
		this.clearRunFollowupTimers();
	}

	clearRunFollowupTimers = () => {
		this.runFollowupTimers.forEach((t) => clearTimeout(t));
		this.runFollowupTimers = [];
	}

	// ---------- Carga de data ----------

	buildLogsParams = () => {
		const { filterJob, filterStatus, filterFrom, filterUntil, page } = this.state;
		const params: any = {
			page,
			limit: PAGE_LIMIT,
			withoutLoading: true,
		};
		if (filterJob) params.job_name = filterJob;
		if (filterStatus) params.status = filterStatus;
		if (filterFrom) params.from = filterFrom;
		if (filterUntil) params.to = filterUntil;
		return params;
	}

	loadAll = async (isFirst: boolean) => {
		const [statusRes, logsRes] = await Promise.allSettled([
			SystemService.admin.getCronStatus({ withoutLoading: true }) as Promise<any>,
			SystemService.admin.getCronLogs(this.buildLogsParams()) as Promise<any>,
		]);

		if (!this.isMounted_) return;

		this.setState({
			status: statusRes.status === 'fulfilled' ? (statusRes.value || null) : null,
			statusError: statusRes.status === 'rejected',
			logs: logsRes.status === 'fulfilled' ? (logsRes.value || null) : null,
			logsError: logsRes.status === 'rejected',
			lastUpdated: new Date(),
			initialLoading: isFirst ? false : this.state.initialLoading,
		});
	}

	reloadStatusOnly = async () => {
		try {
			const res: any = await SystemService.admin.getCronStatus({ withoutLoading: true });
			if (!this.isMounted_) return;
			this.setState({ status: res || null, statusError: false, lastUpdated: new Date() });
		}
		catch (e) {
			if (!this.isMounted_) return;
			this.setState({ statusError: true });
		}
	}

	reloadLogsOnly = async () => {
		try {
			const res: any = await SystemService.admin.getCronLogs(this.buildLogsParams());
			if (!this.isMounted_) return;
			this.setState({ logs: res || null, logsError: false });
		}
		catch (e) {
			if (!this.isMounted_) return;
			this.setState({ logsError: true });
		}
	}

	manualRefresh = () => {
		this.loadAll(false);
	}

	// ---------- Filtros tabla ----------

	updateFilter = (key: 'filterJob' | 'filterStatus' | 'filterFrom' | 'filterUntil', value: string) => {
		this.setState({ [key]: value, page: 1 } as any, () => {
			this.reloadLogsOnly();
		});
	}

	clearFilters = () => {
		this.setState({
			filterJob: '',
			filterStatus: '',
			filterFrom: '',
			filterUntil: '',
			page: 1,
		}, () => this.reloadLogsOnly());
	}

	goToPage = (page: number) => {
		if (page < 1) return;
		this.setState({ page }, () => this.reloadLogsOnly());
	}

	// ---------- Modal: editar config ----------

	openConfigModal = (jobName: string) => {
		const def = JOB_CATALOG[jobName];
		const config = this.state.status?.config || {};
		const form: Record<string, string> = {};
		const keys = def ? def.configKeys : [];
		for (const k of keys) {
			form[k.key] = config[k.key] != null ? String(config[k.key]) : '';
		}
		this.setState({
			configModalOpen: true,
			configModalJob: jobName,
			configForm: form,
		});
	}

	closeConfigModal = () => {
		if (this.state.configSaving) return;
		this.setState({ configModalOpen: false, configModalJob: null, configForm: {} });
	}

	updateConfigField = (key: string, value: string) => {
		this.setState({ configForm: { ...this.state.configForm, [key]: value } });
	}

	saveConfig = async () => {
		const { configModalJob, configForm, status } = this.state;
		if (!configModalJob) return;
		const def = JOB_CATALOG[configModalJob];
		if (!def) return;

		const currentConfig = status?.config || {};
		const changed: { key_name: string; value: string }[] = [];

		for (const k of def.configKeys) {
			const previous = currentConfig[k.key] != null ? String(currentConfig[k.key]) : '';
			const next = configForm[k.key] != null ? String(configForm[k.key]).trim() : '';

			// Validación básica
			if (k.kind === 'number') {
				if (next === '' || isNaN(Number(next))) {
					Globals.showError(`El campo "${ k.label }" debe ser un número válido.`);
					return;
				}
				if (k.min != null && Number(next) < k.min) {
					Globals.showError(`El campo "${ k.label }" no puede ser menor que ${ k.min }.`);
					return;
				}
			}
			if (k.kind === 'bool') {
				if (next !== 'true' && next !== 'false') {
					Globals.showError(`El campo "${ k.label }" debe ser true o false.`);
					return;
				}
			}

			if (next !== previous) {
				changed.push({ key_name: k.key, value: next });
			}
		}

		if (changed.length === 0) {
			Globals.showWarning('No hay cambios para guardar.');
			return;
		}

		this.setState({ configSaving: true });

		try {
			await Promise.all(changed.map((c) =>
				SystemService.admin.updateCronConfig({ key_name: c.key_name, value: c.value })
			));

			if (!this.isMounted_) return;
			this.setState({
				configSaving: false,
				configModalOpen: false,
				configModalJob: null,
				configForm: {},
			});
			Globals.showSuccess(`Configuración de ${ def.label } actualizada.`);
			this.reloadStatusOnly();
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			this.setState({ configSaving: false });
			const msg = e?.response?.data?.error || e?.message || 'No se pudo actualizar la configuración.';
			Globals.showError(msg);
		}
	}

	// Toggle inline enable/disable (sin abrir modal)
	toggleJobEnabled = async (jobName: string, currentlyEnabled: boolean) => {
		const def = JOB_CATALOG[jobName];
		const enabledKey = def ? def.configKeys.find((k) => k.kind === 'bool')?.key : `${ jobName }_enabled`;
		if (!enabledKey) return;

		try {
			await SystemService.admin.updateCronConfig({
				key_name: enabledKey,
				value: currentlyEnabled ? 'false' : 'true',
			});
			Globals.showSuccess(`Job ${ def?.label || jobName } ${ currentlyEnabled ? 'deshabilitado' : 'habilitado' }.`);
			this.reloadStatusOnly();
		}
		catch (e: any) {
			const msg = e?.response?.data?.error || e?.message || 'No se pudo actualizar el estado del job.';
			Globals.showError(msg);
		}
	}

	// ---------- Modal: run now ----------

	openRunModal = (jobName: string) => {
		this.setState({ runModalOpen: true, runModalJob: jobName });
	}

	closeRunModal = () => {
		if (this.state.runSubmitting) return;
		this.setState({ runModalOpen: false, runModalJob: null });
	}

	confirmRunNow = async () => {
		const { runModalJob } = this.state;
		if (!runModalJob) return;
		const def = JOB_CATALOG[runModalJob];

		this.setState({ runSubmitting: true });

		try {
			const res: any = await SystemService.admin.runCronNow({ job_name: runModalJob });
			if (!this.isMounted_) return;
			this.setState({ runSubmitting: false, runModalOpen: false, runModalJob: null });
			const logId = res?.log_id || res?.id;
			Globals.showSuccess(
				`✓ Ejecutado ${ def?.label || runModalJob }${ logId ? ` (log #${ logId })` : '' }`
			);

			// Refresh inmediato: por si el log ya existe (running) y para
			// reflejar is_running_now en la card del job.
			this.reloadStatusOnly();
			this.reloadLogsOnly();

			// Refresh diferido a 1.5s: el endpoint run-now es fire-and-forget;
			// el log puede tardar 100-500ms en aparecer como 'running'.
			// Refresh a 5s: probable transición a 'completed'.
			this.clearRunFollowupTimers();
			const t1 = setTimeout(() => {
				if (!this.isMounted_) return;
				this.reloadStatusOnly();
				this.reloadLogsOnly();
			}, 1500);
			const t2 = setTimeout(() => {
				if (!this.isMounted_) return;
				this.reloadStatusOnly();
				this.reloadLogsOnly();
			}, 5000);
			this.runFollowupTimers = [t1, t2];
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			this.setState({ runSubmitting: false });
			const code = e?.response?.status;
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			let msg = backendMsg || 'No se pudo ejecutar el job.';
			if (code === 422) msg = backendMsg || 'El job está deshabilitado.';
			if (code === 409) msg = backendMsg || 'El job ya está en ejecución.';
			Globals.showError(msg);
		}
	}

	// ---------- Modal: error detail ----------

	openErrorModal = (row: CronLogRow) => {
		this.setState({ errorModalOpen: true, errorModalLog: row });
	}

	closeErrorModal = () => {
		this.setState({ errorModalOpen: false, errorModalLog: null });
	}

	// ============================================================
	// RENDER
	// ============================================================

	renderHeader() {
		const { lastUpdated, initialLoading } = this.state;
		const lastUpdatedLabel = lastUpdated ? humanizeAgo(lastUpdated.toISOString(), this.state.tick) : '—';

		return (
			<div className="cron-header">
				<div className="cron-header-text">
					<h1>Crones</h1>
					<p>Gestión y monitoreo de tareas programadas</p>
				</div>
				<div className="cron-header-actions">
					<span className="refresh-badge">
						<span className="refresh-dot"></span>
						Actualizando cada 10s · { lastUpdatedLabel }
					</span>
					<button
						className="dashboard-refresh-btn"
						onClick={ this.manualRefresh }
						disabled={ initialLoading }>
						{ initialLoading ? 'Cargando…' : 'Refrescar' }
					</button>
				</div>
			</div>
		);
	}

	renderJobCard(jobName: string, job: CronJob | undefined) {
		const def = JOB_CATALOG[jobName];
		const label = def?.label || jobName;
		const description = def?.description || '';
		const accent = def?.color || '#54aa3f';

		// Si el job no vino del backend, mostrar card "sin data"
		if (!job) {
			return (
				<div key={ jobName } className="job-card unknown" style={ { borderTopColor: accent } }>
					<div className="job-card__header">
						<span className="job-card__name">{ label }</span>
						<span className="badge unknown">Sin data</span>
					</div>
					<p className="job-card__desc">No hay datos disponibles para este job.</p>
				</div>
			);
		}

		const kind = statusKind(job.last_status, job.is_running_now);
		const enabled = !!job.enabled;
		const cardClass = enabled ? kind : 'disabled';

		// Próximo run
		let nextRunDate: Date | null = null;
		let countdownLabel = '(deshabilitado)';
		let nextRunAbsolute = '—';
		if (enabled) {
			nextRunDate = nextRunFromCron(job.schedule_cron, this.state.status?.server_time || undefined);
			if (nextRunDate) {
				const msUntil = nextRunDate.getTime() - this.state.tick;
				countdownLabel = formatCountdown(msUntil);
				nextRunAbsolute = moment(nextRunDate).utcOffset(-4).format('DD/MM HH:mm:ss');
			}
			else {
				countdownLabel = '—';
			}
		}

		// Stats: el backend puede traerlas en stats_24h, stats o last_stats
		const stats = job.stats_24h || job.stats || job.last_stats || {};
		const successful = safeNumber((stats as any).successful);
		const failed = safeNumber((stats as any).failed);
		const runs = safeNumber((stats as any).runs ?? (stats as any).processed ?? (successful + failed));

		const lastBadgeLabel = job.is_running_now
			? 'RUNNING'
			: (job.last_status ? String(job.last_status).toUpperCase() : '—');

		return (
			<div key={ jobName } className={ `job-card ${ cardClass }` } style={ { borderTopColor: accent } }>
				<div className="job-card__header">
					<div>
						<span className="job-card__name">{ label }</span>
						{ description && <p className="job-card__desc">{ description }</p> }
					</div>
					<div className="job-card__top-right">
						<span className={ `badge ${ enabled ? kind : 'disabled' }` }>
							{ !enabled ? 'DISABLED' : (job.is_running_now ? 'RUNNING' : (job.last_status ? String(job.last_status).toUpperCase() : 'IDLE')) }
						</span>
						<label className="switch" title={ enabled ? 'Deshabilitar' : 'Habilitar' }>
							<input
								type="checkbox"
								checked={ enabled }
								onChange={ () => this.toggleJobEnabled(jobName, enabled) }
							/>
							<span className="switch-slider"></span>
						</label>
					</div>
				</div>

				<div className="job-card__schedule">
					<span className="job-card__schedule-desc">{ job.schedule_description || '—' }</span>
					{
						job.schedule_cron && (
							<code className="job-card__schedule-cron">{ job.schedule_cron }</code>
						)
					}
				</div>

				<div className="job-card__section">
					<span className="job-card__section-label">Próxima ejecución</span>
					<div className="job-card__next-run">
						<span className="job-card__countdown">{ countdownLabel }</span>
						{
							enabled && nextRunDate && (
								<span className="job-card__next-abs">{ nextRunAbsolute }</span>
							)
						}
					</div>
				</div>

				<div className="job-card__section">
					<span className="job-card__section-label">Último run (24h)</span>
					<div className="job-card__last-run">
						<span className={ `badge sm ${ kind }` }>{ lastBadgeLabel }</span>
						<span className="job-card__last-time">{ humanizeAgo(job.last_run, this.state.tick) }</span>
						{
							job.last_error_message && (
								<span
									className="job-card__error-marker"
									title={ job.last_error_message }>
									⚠
								</span>
							)
						}
					</div>
					<div className="job-card__stats">
						<span>
							<strong>{ successful }</strong>
							{ ' / ' }
							<span>{ runs }</span>
						</span>
						{ failed > 0 && <span className="critical">{ failed } fallidos</span> }
						{ job.last_duration_ms != null && <span className="muted">{ formatDuration(job.last_duration_ms) }</span> }
					</div>
				</div>

				<div className="job-card__actions">
					<button
						className="cron-btn cron-btn-primary"
						onClick={ () => this.openRunModal(jobName) }
						disabled={ !enabled || !!job.is_running_now }>
						Run Now
					</button>
					<button
						className="cron-btn cron-btn-secondary"
						onClick={ () => this.openConfigModal(jobName) }
						disabled={ !def }>
						Editar config
					</button>
				</div>
			</div>
		);
	}

	renderJobs() {
		const { status, statusError, initialLoading } = this.state;

		if (statusError) {
			return (
				<div className="cron-error-card">
					No se pudo cargar el estado de los crones. Reintentando en 10s…
				</div>
			);
		}

		if (initialLoading && !status) {
			return (
				<div className="cron-grid">
					{
						KNOWN_JOBS.map((jobName) => (
							<div key={ jobName } className="job-card skeleton">
								<div className="skeleton-line" style={ { width: '40%', height: 14 } }></div>
								<div className="skeleton-line" style={ { width: '70%', height: 10, marginTop: 12 } }></div>
								<div className="skeleton-line" style={ { width: '50%', height: 10, marginTop: 8 } }></div>
								<div className="skeleton-line" style={ { width: '90%', height: 10, marginTop: 20 } }></div>
							</div>
						))
					}
				</div>
			);
		}

		const jobsFromBackend = Array.isArray(status?.jobs) ? status!.jobs as CronJob[] : [];
		const byName: Record<string, CronJob> = {};
		jobsFromBackend.forEach((j) => {
			if (j.job_name) byName[j.job_name] = j;
		});

		// Render en el orden de KNOWN_JOBS para tener un grid estable; al final
		// agregamos cualquier job extra que venga del backend pero no esté en
		// el catálogo (defensive).
		const extras = jobsFromBackend
			.map((j) => j.job_name || '')
			.filter((n) => n && !KNOWN_JOBS.includes(n));

		return (
			<div className="cron-grid">
				{ KNOWN_JOBS.map((jobName) => this.renderJobCard(jobName, byName[jobName])) }
				{ extras.map((jobName) => this.renderJobCard(jobName, byName[jobName])) }
			</div>
		);
	}

	renderFilters() {
		const { filterJob, filterStatus, filterFrom, filterUntil } = this.state;

		const jobOptions: { value: string; label: string }[] = [
			{ value: '', label: 'Todos' },
			...KNOWN_JOBS.map((j) => ({ value: j, label: JOB_CATALOG[j].label })),
		];

		return (
			<div className="filters-bar">
				<div className="filter-field">
					<label>Job</label>
					<select
						className="filter-select"
						value={ filterJob }
						onChange={ (e) => this.updateFilter('filterJob', e.target.value) }>
						{ jobOptions.map((o) => <option key={ o.value } value={ o.value }>{ o.label }</option>) }
					</select>
				</div>
				<div className="filter-field">
					<label>Status</label>
					<select
						className="filter-select"
						value={ filterStatus }
						onChange={ (e) => this.updateFilter('filterStatus', e.target.value) }>
						{ STATUS_OPTIONS.map((o) => <option key={ o.value } value={ o.value }>{ o.label }</option>) }
					</select>
				</div>
				<div className="filter-field">
					<label>Desde</label>
					<input
						type="date"
						className="filter-input"
						value={ filterFrom }
						onChange={ (e) => this.updateFilter('filterFrom', e.target.value) }
					/>
				</div>
				<div className="filter-field">
					<label>Hasta</label>
					<input
						type="date"
						className="filter-input"
						value={ filterUntil }
						onChange={ (e) => this.updateFilter('filterUntil', e.target.value) }
					/>
				</div>
				<button
					className="cron-btn cron-btn-ghost"
					onClick={ this.clearFilters }>
					Limpiar
				</button>
			</div>
		);
	}

	renderLogsTable() {
		const { logs, logsError, initialLoading, page } = this.state;

		if (logsError) {
			return (
				<div className="cron-error-card">
					No se pudo cargar el historial de logs.
				</div>
			);
		}

		const rows = Array.isArray(logs?.logs) ? logs!.logs as CronLogRow[] : [];
		const totalPagesRaw = safeNumber(logs?.pagination?.total_pages);
		const totalPages = totalPagesRaw > 0 ? totalPagesRaw : 1;

		return (
			<div className="logs-table-wrap">
				<table className="logs-table">
					<thead>
						<tr>
							<th>Fecha</th>
							<th>Job</th>
							<th>Status</th>
							<th>Duración</th>
							<th>Procesados</th>
							<th>Exitosos</th>
							<th>Fallidos</th>
							<th>Error</th>
						</tr>
					</thead>
					<tbody>
						{
							rows.map((row, idx) => {
								const kind = statusKind(row.status);
								const def = row.job_name ? JOB_CATALOG[row.job_name] : null;
								const jobColor = def?.color || '#969aab';
								const jobLabel = def?.label || row.job_name || '—';
								const fecha = row.started_at ? moment(row.started_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss') : '—';
								const err = row.error_message || '';
								return (
									<tr key={ row.id ?? `${ row.started_at }-${ idx }` }>
										<td className="mono">{ fecha }</td>
										<td>
											<span
												className="job-pill"
												style={ { borderColor: jobColor, color: jobColor } }>
												{ jobLabel }
											</span>
										</td>
										<td><span className={ `badge sm ${ kind }` }>{ (row.status || '—').toUpperCase() }</span></td>
										<td className="mono">{ formatDuration(row.duration_ms) }</td>
										<td className="mono">{ row.total_processed != null ? formatNumber(row.total_processed) : '—' }</td>
										<td className="mono ok">{ row.successful_updates != null ? formatNumber(row.successful_updates) : '—' }</td>
										<td className={ `mono ${ safeNumber(row.failed_updates) > 0 ? 'critical' : '' }` }>
											{ row.failed_updates != null ? formatNumber(row.failed_updates) : '—' }
										</td>
										<td>
											{
												err ? (
													<button
														className="link-error"
														onClick={ () => this.openErrorModal(row) }
														title={ err }>
														{ err.length > 60 ? `${ err.substring(0, 60) }…` : err }
													</button>
												) : (
													<span className="muted">—</span>
												)
											}
										</td>
									</tr>
								);
							})
						}
						{
							rows.length === 0 && !initialLoading && (
								<tr>
									<td colSpan={ 8 } className="empty-row">
										No hay registros para los filtros seleccionados.
									</td>
								</tr>
							)
						}
						{
							rows.length === 0 && initialLoading && (
								<tr>
									<td colSpan={ 8 } className="empty-row">Cargando…</td>
								</tr>
							)
						}
					</tbody>
				</table>

				{
					totalPages > 1 && (
						<div className="logs-pagination">
							<Pagination
								active={ page }
								pages={ totalPages }
								onChange={ (p: number) => this.goToPage(p) }
							/>
						</div>
					)
				}
			</div>
		);
	}

	renderConfigModal() {
		const { configModalOpen, configModalJob, configForm, configSaving } = this.state;
		if (!configModalOpen || !configModalJob) return null;

		const def = JOB_CATALOG[configModalJob];
		const title = `Editar config — ${ def?.label || configModalJob }`;

		return (
			<Modal
				className="modal-cron-config"
				visible={ configModalOpen }
				title={ title }
				onClose={ this.closeConfigModal }>
				<div className="modal-cron-body">
					{ def?.description && <p className="modal-desc">{ def.description }</p> }

					<div className="config-fields">
						{
							(def?.configKeys || []).map((field) => {
								const value = configForm[field.key] ?? '';
								if (field.kind === 'bool') {
									const enabled = value === 'true';
									return (
										<div key={ field.key } className="config-field config-field-bool">
											<label>{ field.label }</label>
											<label className="switch">
												<input
													type="checkbox"
													checked={ enabled }
													disabled={ configSaving }
													onChange={ (e) => this.updateConfigField(field.key, e.target.checked ? 'true' : 'false') }
												/>
												<span className="switch-slider"></span>
											</label>
											<span className="config-field-hint">{ enabled ? 'Habilitado' : 'Deshabilitado' }</span>
										</div>
									);
								}
								return (
									<div key={ field.key } className="config-field">
										<label>{ field.label }</label>
										<input
											type="number"
											min={ field.min }
											className="filter-input"
											disabled={ configSaving }
											value={ value }
											onChange={ (e) => this.updateConfigField(field.key, e.target.value) }
										/>
									</div>
								);
							})
						}
					</div>

					<div className="modal-actions">
						<button
							className="cron-btn cron-btn-ghost"
							onClick={ this.closeConfigModal }
							disabled={ configSaving }>
							Cancelar
						</button>
						<button
							className="cron-btn cron-btn-primary"
							onClick={ this.saveConfig }
							disabled={ configSaving }>
							{ configSaving ? 'Guardando…' : 'Guardar cambios' }
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	renderRunModal() {
		const { runModalOpen, runModalJob, runSubmitting } = this.state;
		if (!runModalOpen || !runModalJob) return null;

		const def = JOB_CATALOG[runModalJob];

		return (
			<Modal
				className="modal-cron-run"
				visible={ runModalOpen }
				title={ `¿Ejecutar el cron ${ def?.label || runModalJob } ahora?` }
				onClose={ this.closeRunModal }>
				<div className="modal-cron-body">
					<p className="modal-desc">
						{ def?.description || 'Este job se ejecutará inmediatamente.' }
					</p>
					<p className="modal-warn">
						La ejecución manual no respeta el intervalo programado. Asegúrate de que el job no esté corriendo.
					</p>

					<div className="modal-actions">
						<button
							className="cron-btn cron-btn-ghost"
							onClick={ this.closeRunModal }
							disabled={ runSubmitting }>
							Cancelar
						</button>
						<button
							className="cron-btn cron-btn-primary"
							onClick={ this.confirmRunNow }
							disabled={ runSubmitting }>
							{ runSubmitting ? 'Ejecutando…' : 'Ejecutar' }
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	renderErrorModal() {
		const { errorModalOpen, errorModalLog } = this.state;
		if (!errorModalOpen || !errorModalLog) return null;

		const def = errorModalLog.job_name ? JOB_CATALOG[errorModalLog.job_name] : null;
		const title = `Error — ${ def?.label || errorModalLog.job_name || 'Job' }`;
		const fecha = errorModalLog.started_at ? moment(errorModalLog.started_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss') : '—';
		let metadataText = '';
		if (errorModalLog.metadata != null) {
			try {
				metadataText = typeof errorModalLog.metadata === 'string'
					? errorModalLog.metadata
					: JSON.stringify(errorModalLog.metadata, null, 2);
			}
			catch { metadataText = String(errorModalLog.metadata); }
		}

		return (
			<Modal
				className="modal-cron-error"
				visible={ errorModalOpen }
				title={ title }
				onClose={ this.closeErrorModal }>
				<div className="modal-cron-body">
					<div className="error-meta">
						<span className="info-label">Fecha</span>
						<span className="info-value">{ fecha }</span>
					</div>
					{
						errorModalLog.error_message && (
							<div className="error-block">
								<span className="info-label">Mensaje</span>
								<pre className="error-pre">{ errorModalLog.error_message }</pre>
							</div>
						)
					}
					{
						metadataText && (
							<div className="error-block">
								<span className="info-label">Metadata</span>
								<pre className="error-pre">{ metadataText }</pre>
							</div>
						)
					}
					<div className="modal-actions">
						<button
							className="cron-btn cron-btn-ghost"
							onClick={ this.closeErrorModal }>
							Cerrar
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	render() {
		return (
			<div className="system-page cron-page">
				{ this.renderHeader() }
				{ this.renderJobs() }

				<div className="cron-logs-section">
					<div className="cron-logs-header">
						<h3>Historial de ejecuciones</h3>
					</div>
					{ this.renderFilters() }
					{ this.renderLogsTable() }
				</div>

				{ this.renderConfigModal() }
				{ this.renderRunModal() }
				{ this.renderErrorModal() }
			</div>
		);
	}
}

const formatNumber = (v: any): string => {
	const n = Number(v);
	if (isNaN(n)) return '0';
	return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default Cron;
